import React, { useRef } from "react";
import { Formik, Form } from "formik";
import { useReactToPrint } from "react-to-print";
import { OverlayLoader, NeoButton, NeoInput, NeoEmptyState, NeoCard } from "../../../../../components/Common";
import WorkoutTabsEditor from "../editor/WorkoutTabsEditor";
import PrintableWorkout from "../print/PrintableWorkout";
import PoolDeckPrintableWorkout from "../swimming/PoolDeckPrintableWorkout";
import { useTenant } from "../../../../../hooks/useTenant";
import { STRENGTH_SPLIT_OPTIONS, WORKOUT_LEVELS, WORKOUT_OBJECTIVES, WORKOUT_TYPES } from "../../constants";
import { getTemplateSummary, normalizeTemplateWorkoutType } from "../../utils/workoutSummary";

const BRANCH_TO_WORKOUT_TYPE = {
    musculacao: "strength",
    crossfit: "wod",
    natacao: "swimming",
    corrida: "running",
};

/**
 * Editor de modelo de treino (template).
 * SEM aluno, SEM data. Foco nos blocos/exercícios.
 * Campos extras de musculação (frequência, semanas) aparecem condicionalmente.
 */
const TemplateEditor = ({
    initialValues,
    onSave,
    isSaving,
    onDelete,
    onPrescribe,
    onAiGenerate,
}) => {
    const printRef = useRef();
    const { tenantName, tenantLogo, primaryBranch } = useTenant();

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        content: () => printRef.current,
        documentTitle: initialValues?.title ? `Modelo_${initialValues.title}` : "Modelo_de_Treino",
    });

    if (!initialValues) {
        return (
            <NeoEmptyState
                icon="mdi-clipboard-text-outline"
                title="Selecione ou crie um modelo"
                description="Escolha um modelo na lista ao lado ou crie um novo."
            />
        );
    }

    const safeValues = {
        title: "",
        description: "",
        workoutType: "strength",
        objective: "",
        level: "",
        tags: [],
        weeklyFrequency: 3,
        totalWeeks: 4,
        strengthSplit: "custom",
        sessions: [],
        workouts: [],
        blocks: [],
        ...initialValues,
    };

    // Opções de modalidade filtradas pelo ramo da academia
    const getAllowedTypes = () => {
        const allowed = primaryBranch?.length > 0
            ? primaryBranch.map(b => BRANCH_TO_WORKOUT_TYPE[b]).filter(Boolean)
            : null;
        return allowed ? WORKOUT_TYPES.filter(t => allowed.includes(t.value)) : WORKOUT_TYPES;
    };

    return (
        <Formik
            enableReinitialize
            initialValues={safeValues}
            onSubmit={(values) => onSave(values)}
        >
            {({ values, handleChange, setFieldValue, setValues, isSubmitting }) => {
                const summary = getTemplateSummary(values);

                const headerActions = (
                    <div className="d-flex gap-2 align-items-center">
                        {values.id && onDelete && (
                            <NeoButton
                                type="button"
                                variant="soft-danger"
                                size="sm"
                                square
                                icon="mdi-trash-can-outline"
                                title="Excluir Modelo"
                                onClick={() => onDelete(values.id, values.title)}
                            />
                        )}
                        {onAiGenerate && !values.id && (
                            <NeoButton
                                type="button"
                                variant="soft-info"
                                size="sm"
                                icon="mdi-robot-outline"
                                onClick={onAiGenerate}
                            >
                                Gerar com IA
                            </NeoButton>
                        )}
                        <NeoButton
                            type="button"
                            variant="soft-secondary"
                            size="sm"
                            square
                            icon="mdi-printer"
                            title="Imprimir Modelo"
                            onClick={() => handlePrint()}
                        />
                        <NeoButton
                            variant="primary"
                            type="submit"
                            size="sm"
                            loading={isSaving}
                            disabled={isSubmitting || isSaving}
                            icon="mdi-content-save-outline"
                        >
                            Salvar Modelo
                        </NeoButton>
                    </div>
                );

                return (
                    <Form>
                        <NeoCard
                            title={values.id ? "Editar Modelo" : "Novo Modelo de Treino"}
                            subtitle={`${summary.typeConfig.label} • ${summary.mainMetric} • ${summary.sessionsCount || 0} sessão${summary.sessionsCount !== 1 ? "s" : ""}`}
                            headerIcon={summary.typeConfig.icon}
                            headerActions={headerActions}
                            bodyClassName="p-4 bg-light-subtle pb-5"
                        >
                            <OverlayLoader show={isSaving} label="Salvando..." />

                            {/* ── Dados do Modelo ── */}
                            <NeoCard thinBorder className="mb-4" bodyClassName="p-4 bg-white">
                                <div className="row g-3 align-items-start">
                                    <div className="col-md-5">
                                        <NeoInput
                                            id="title"
                                            name="title"
                                            label="Nome do Modelo *"
                                            type="text"
                                            onChange={handleChange}
                                            value={values.title}
                                            placeholder="Ex: Treino de Adaptação"
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <NeoInput
                                            label="Modalidade"
                                            type="select"
                                            name="workoutType"
                                            value={values.workoutType || "strength"}
                                            onChange={(event) => {
                                                const nextType = event.target.value;
                                                setValues(normalizeTemplateWorkoutType({
                                                    ...values,
                                                    workoutType: nextType,
                                                    objective: "",
                                                }, nextType));
                                            }}
                                        >
                                            {getAllowedTypes().map(type => (
                                                <option key={type.value} value={type.value}>{type.label}</option>
                                            ))}
                                        </NeoInput>
                                    </div>

                                    <div className="col-md-4">
                                        <NeoInput
                                            label="Formato do Modelo"
                                            type="select"
                                            value={values.isSingleDay ? "single" : "cycle"}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === "single") {
                                                    setFieldValue("isSingleDay", true);
                                                    setFieldValue("weeklyFrequency", 1);
                                                } else {
                                                    setFieldValue("isSingleDay", false);
                                                }
                                            }}
                                        >
                                            <option value="cycle">Ciclo / Ficha (Múltiplas Semanas)</option>
                                            <option value="single">Data Específica / Multisport (1 Dia)</option>
                                        </NeoInput>
                                    </div>

                                    <div className="col-md-4">
                                        <NeoInput
                                            label="Objetivo"
                                            type="select"
                                            name="objective"
                                            value={values.objective || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Selecione...</option>
                                            {(WORKOUT_OBJECTIVES[values.workoutType || "strength"] || []).map(objective => (
                                                <option key={objective} value={objective}>{objective}</option>
                                            ))}
                                        </NeoInput>
                                    </div>

                                    <div className="col-md-4">
                                        <NeoInput
                                            label="Nível"
                                            type="select"
                                            name="level"
                                            value={values.level || ""}
                                            onChange={handleChange}
                                        >
                                            <option value="">Selecione...</option>
                                            {WORKOUT_LEVELS.map(level => (
                                                <option key={level} value={level}>{level}</option>
                                            ))}
                                        </NeoInput>
                                    </div>

                                    <div className="col-md-4">
                                        <NeoInput
                                            label="Tags"
                                            type="text"
                                            name="tags"
                                            value={Array.isArray(values.tags) ? values.tags.join(", ") : values.tags || ""}
                                            onChange={(event) => setFieldValue("tags", event.target.value)}
                                            placeholder="ex: iniciante, manhã, competição"
                                        />
                                    </div>

                                    {values.workoutType === "strength" && (
                                        <div className="col-md-4">
                                            <NeoInput
                                                label="Divisão da ficha"
                                                type="select"
                                                name="strengthSplit"
                                                value={values.strengthSplit || "custom"}
                                                onChange={handleChange}
                                            >
                                                {STRENGTH_SPLIT_OPTIONS.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </NeoInput>
                                        </div>
                                    )}

                                    {!values.isSingleDay && (
                                        <>
                                            <div className="col-md-4">
                                                <NeoInput
                                                    id="weeklyFrequency"
                                                    name="weeklyFrequency"
                                                    label="Dias por semana"
                                                    type="number"
                                                    min="1"
                                                    max="7"
                                                    onChange={handleChange}
                                                    value={values.weeklyFrequency}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <NeoInput
                                                    id="totalWeeks"
                                                    name="totalWeeks"
                                                    label="Semanas (Duração)"
                                                    type="number"
                                                    min="1"
                                                    max="52"
                                                    onChange={handleChange}
                                                    value={values.totalWeeks}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </NeoCard>

                            <div className="mt-4 px-2 pb-4">
                                <WorkoutTabsEditor
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    handleChange={handleChange}
                                />
                            </div>
                        </NeoCard>

                        {/* ── Componente Oculto para Impressão ── */}
                        <div style={{ display: "none" }}>
                            <div ref={printRef}>
                                {(values.sessions || values.workouts || []).map((w, index) => (
                                    <div key={index} style={{ pageBreakAfter: index < (values.sessions || values.workouts || []).length - 1 ? "always" : "auto" }}>
                                        {values.workoutType === "swimming" ? (
                                            <PoolDeckPrintableWorkout
                                                workout={w}
                                                templateTitle={values.title || "Modelo de Natação"}
                                                templateDescription={values.description}
                                                tenantName={tenantName || "Modelo de Treino"}
                                                tenantLogo={tenantLogo}
                                            />
                                        ) : (
                                            <PrintableWorkout
                                                workout={{
                                                    ...w,
                                                    workoutType: values.workoutType,
                                                    clubSession: w.clubSession || values.clubSession,
                                                    cyclePlan: w.cyclePlan || values.cyclePlan,
                                                    attendancePlan: w.attendancePlan || values.attendancePlan,
                                                    title: `${values.title || 'Modelo'} - ${w.name}`,
                                                    description: values.description
                                                }}
                                                tenantName={tenantName || "Modelo de Treino"}
                                                tenantLogo={tenantLogo}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                    </Form>
                );
            }}
        </Formik>
    );
};

export default TemplateEditor;
